/** rrweb event type: FullSnapshot */
const RRWEB_FULL_SNAPSHOT = 2;
/** rrweb event type: IncrementalSnapshot */
const RRWEB_INCREMENTAL = 3;
/** IncrementalSource.Mutation */
const RRWEB_MUTATION = 0;
/** IncrementalSource.Input */
const RRWEB_INPUT = 5;

type SerializedNode = {
  id?: number;
  type?: number;
  tagName?: string;
  attributes?: Record<string, unknown>;
  childNodes?: unknown[];
};

function attrIsMasked(attrs: Record<string, unknown> | undefined): boolean {
  if (!attrs) return false;
  const v = attrs["data-orbit-masked"];
  return v === true || v === "true" || v === "";
}

function redact(value: unknown): string {
  return "*".repeat(String(value ?? "").length);
}

function isFormControl(tagName: string | undefined): boolean {
  const tag = (tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select";
}

/**
 * Walk a serialized rrweb node tree. Any node under data-orbit-masked
 * (or the marked node itself) has its id recorded and input values starred.
 */
function walkSerialized(
  node: unknown,
  ancestorMasked: boolean,
  maskedIds: Set<number>,
): void {
  if (!node || typeof node !== "object") return;
  const n = node as SerializedNode;
  const tagged = attrIsMasked(n.attributes);
  const masked = ancestorMasked || tagged;

  if (masked && typeof n.id === "number") {
    maskedIds.add(n.id);
  }

  if (masked && n.attributes && isFormControl(n.tagName) && n.attributes.value != null) {
    n.attributes.value = redact(n.attributes.value);
  }

  if (Array.isArray(n.childNodes)) {
    for (const child of n.childNodes) {
      walkSerialized(child, masked, maskedIds);
    }
  }
}

/**
 * rrweb 2.0.0-alpha.4 calls maskInputFn(text) without the element, so
 * per-field masking cannot run inside rrweb. Redact marked fields on emit.
 */
export function sanitizeRecordedEvent(
  event: unknown,
  maskedIds: Set<number>,
): void {
  if (!event || typeof event !== "object") return;
  const ev = event as { type?: number; data?: Record<string, unknown> };

  if (ev.type === RRWEB_FULL_SNAPSHOT && ev.data) {
    walkSerialized(ev.data.node, false, maskedIds);
    return;
  }

  if (ev.type !== RRWEB_INCREMENTAL || !ev.data) return;

  const data = ev.data as {
    source?: number;
    id?: number;
    text?: unknown;
    adds?: Array<{ node?: unknown; parentId?: number }>;
    attributes?: Array<{
      id?: number;
      attributes?: Record<string, unknown>;
    }>;
  };

  if (data.source === RRWEB_INPUT && typeof data.id === "number" && maskedIds.has(data.id)) {
    if (data.text != null) data.text = redact(data.text);
    return;
  }

  if (data.source !== RRWEB_MUTATION) return;

  if (Array.isArray(data.adds)) {
    for (const add of data.adds) {
      const parentMasked =
        typeof add.parentId === "number" && maskedIds.has(add.parentId);
      walkSerialized(add.node, parentMasked, maskedIds);
    }
  }

  if (Array.isArray(data.attributes)) {
    for (const rec of data.attributes) {
      if (
        typeof rec.id === "number" &&
        maskedIds.has(rec.id) &&
        rec.attributes &&
        rec.attributes.value != null
      ) {
        rec.attributes.value = redact(rec.attributes.value);
      }
    }
  }
}
